import { fetchListWithdrawRequestPazirande } from "@/api/merchant/request.post";
import { numberSeparator, toPersianNumber } from "@/hooks/useThousandsSeparator";
import AdapterMomentJalali from "@/shared/components/common/datePeacker";
import CustomSelect from "@/shared/components/common/select";
import CustomTable from "@/shared/components/common/table";
import BoxKit from "@/shared/components/kit/box";
import CustomButton from "@/shared/components/kit/button";
import StackKit from "@/shared/components/kit/stack";
import TypoGraphyKit from "@/shared/components/kit/typoGraphy";
import { Grid, TextField } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import moment from "jalali-moment";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
/**
 * ContentWalletWithdrawalRequestReports
 *
 * کامپوننت گزارش درخواست‌های برداشت کیف‌پول پذیرنده.
 * این کامپوننت شامل فیلترهای انتخاب تعداد تراکنش، تاریخ، جستجو، و جدول نمایش اطلاعات می‌باشد.
 * همچنین امکان دریافت فایل CSV از تراکنش‌ها را فراهم می‌کند.
 *
 * ویژگی‌های کلیدی:
 * - واکنش‌گرا (Responsive) برای نمایش در دستگاه‌های مختلف
 * - استفاده از Material UI Grid و Stack برای چیدمان
 * - ادغام با useMutation برای دریافت داده‌ها از API
 * - پشتیبانی از تاریخ جلالی (Jalali)
 *
 * @component
 * @returns JSX.Element
 */

const ContentWalletWithdrawalRequestReports = () => {
  const tabs = ["روزانه", "هفتگی", "ماهانه"];
  const [activeTab, setActiveTab] = useState("روزانه");
  const [dateValue, setDateValue] = useState<Date | string | null>(null);
  const [depositWallet, setDepositWallet] = useState<any>();
  const [value, setValue] = useState(5);
  const [searchValue, setSearchValue] = useState("");

  const { mutate: submitFormWalletPazirande } = useMutation({
    mutationFn: fetchListWithdrawRequestPazirande,
    onSuccess: (data: any) => setDepositWallet(data?.data),
    onError: (error: AxiosError<{ message: string }>) =>
      toast.error(error?.response?.data?.message || "خطا در ثبت اطلاعات"),
  });

  const handleChange = (val: any) => setValue(val);

  useEffect(() => {
    const requestBody = {
      date: dateValue ? moment(dateValue, "jYYYY/jMM/jDD").format("YYYY-MM-DD") : moment(new Date()).format("YYYY-MM-DD"),
      search: searchValue,
      type: "monthly",
      count: value.toString(),
    };
    submitFormWalletPazirande(requestBody);
  }, []);

  const handleSearch = () => {
    const requestBody = {
      date: dateValue && moment(dateValue, "jYYYY/jMM/jDD").format("YYYY-MM-DD"),
      search: searchValue,
      type: "monthly",
      count: value.toString(),
    };
    submitFormWalletPazirande(requestBody);
  };

  const handleDownloadCSV = () => {
    if (!depositWallet?.withdraws?.length) return;
    const headers = [
      "ردیف",
      "مبلغ برداشت (ریال)",
      "ساعت و تاریخ درخواست",
      "مبدا تراکنش",
      "مقصد تراکنش",
      "وضعیت درخواست",
      "توضیحات",
    ];

    const rows = depositWallet.withdraws.map((item: any, index: number) => [
      toPersianNumber(index + 1),
      toPersianNumber(numberSeparator(item.price)),
      moment(item.created_at).locale("fa").format("jYYYY/jMM/jDD HH:mm"),
      toPersianNumber(item.source_card),
      toPersianNumber(item.target_card),
      item.status,
      item.detaile ?? "",
    ]);

    const csvContent = [headers, ...rows].map((e) => e.map((v: any) => `"${v}"`).join(",")).join("\n");

    const blob = new Blob(["\uFEFF" + csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "transactions.csv";
    link.click();
  };

  const dataHeader = [
    { id: "rowId", title: "ردیف" },
    { id: "price", title: "مبلغ برداشت (ریال)" },
    { id: "created_at", title: "ساعت و تاریخ درخواست" },
    { id: "source_card", title: "مبدا تراکنش" },
    { id: "target_card", title: "مقصد تراکنش" },
    { id: "status", title: "وضعیت درخواست" },
    { id: "detaile", title: "توضیحات" },
  ];

  const fakeData =
    depositWallet?.withdraws?.map((item: any, index: number) => ({
      ...item,
      rowId: toPersianNumber(index + 1),
      source_card: (
        <TypoGraphyKit sx={{ direction: "ltr" }}>
          {toPersianNumber(item.source_card)}
        </TypoGraphyKit>
      ),
      target_card: (
        <TypoGraphyKit sx={{ direction: "ltr" }}>
          {toPersianNumber(item.target_card)}
        </TypoGraphyKit>
      ),
      created_at: `${toPersianNumber(
        moment(item.created_at).locale("fa").format("HH:mm")
      )} ${toPersianNumber(
        moment(item.created_at).locale("fa").format("jYYYY/jMM/jDD")
      )}`,
      price: numberSeparator(item.price),
      status: item.status === "pending" ? "درحال بررسی" : item.status,
    })) ?? [];

  return (
    <StackKit sx={{ width: "100%", }} gap={3} justifyContent="space-between">
      <Grid container spacing={2}>
        {/* Filters */}
        <Grid size={{ xs: 12 }}>
          <StackKit direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", md: "center" }} gap={2} sx={{ flexWrap: "wrap" }}>
            {/* Left Controls */}
            <StackKit direction={{ xs: "column", sm: "row" }} gap={2} alignItems="center" flexWrap="wrap" sx={{ width: { xs: "100%", md: "auto" } }}>
              <CustomSelect
                label={
                  <TypoGraphyKit variant="body1" sx={{ color: "#A4A4A4" }}>
                    تراکنش آخر
                  </TypoGraphyKit>
                } options={[5, 10, 15, 20]} onChange={handleChange} value={value || 10}
              />

              <StackKit direction="row" flexWrap="wrap" justifyContent="center" alignItems="center"
                sx={{ bgcolor: "#5A5A5A", p: 1.2, borderRadius: 2, width: { xs: "100%", sm: "auto" }, overflowX: "auto", "& > :not(style) ~ :not(style)": { marginLeft: "0 !important", }, }}>
                {tabs.map((tab) => (
                  <StackKit
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    sx={{
                      px: 2,
                      py: 1,
                      borderRadius: 2,
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      whiteSpace: "nowrap",
                      width: { xs: '100%', sm: 'unset' },
                      bgcolor: activeTab === tab ? "#434343" : "transparent",
                      color: activeTab === tab ? "#fff" : "#B3B3B3",
                      border: activeTab === tab ? "1px solid #434343" : "1px solid transparent",
                      "&:hover": { bgcolor: activeTab === tab ? "#434343" : "#666666", },
                    }}
                  >
                    <TypoGraphyKit variant="body2" sx={{ color: "inherit" }}>
                      {tab}
                    </TypoGraphyKit>
                  </StackKit>
                ))}
              </StackKit>
            </StackKit>

            {/* Right Controls */}
            <StackKit direction={{ xs: "column", md: "row" }} alignItems="center" justifyContent="space-between" gap={2}>
              <AdapterMomentJalali heightProps="45px"
                valueDate={
                  dateValue ? moment(dateValue, "jYYYY/jMM/jDD") : null
                }
                handleChange={(newValue: any) => {
                  const formatted = newValue?.format("jYYYY-jMM-jDD");
                  setDateValue(formatted);
                }}
              />
              <TextField placeholder="وارد کردن جزئیات جستجو" fullWidth value={searchValue} onChange={(e) => setSearchValue(e.target.value)} sx={{ flex: 1, width: "100%", "& .MuiInputBase-root": { height: "45px" }, }} />
              <CustomButton onClick={handleSearch} sx={{ px: 3, height: "45px", whiteSpace: "nowrap", width: { xs: "100%", sm: "auto" }, }}>
                <TypoGraphyKit variant="body1">جستجو</TypoGraphyKit>
              </CustomButton>
            </StackKit>
          </StackKit>
        </Grid>

        {/* Table Section */}
        <Grid size={{ xs: 12 }}>
          <BoxKit sx={{ overflowX: "auto" }}>
            <CustomTable dataHeader={dataHeader} dataTbody={fakeData} />
          </BoxKit>
        </Grid>

        {/* Download Button */}
        <Grid size={{ xs: 12 }}>
          <StackKit direction="row" justifyContent={{ xs: "center", md: "flex-end" }} alignItems="center">
            <CustomButton sx={{ bgcolor: "#5A5A5A", py: 1.5, px: 3, borderRadius: 2, width: { xs: "100%", sm: "auto" }, }} onClick={handleDownloadCSV}>
              <TypoGraphyKit variant="body1">
                دریافت صورتحساب به صورت فایل
              </TypoGraphyKit>
            </CustomButton>
          </StackKit>
        </Grid>
      </Grid>
    </StackKit>
  );
};

export default ContentWalletWithdrawalRequestReports;
